import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { HotTable } from '@handsontable/react-wrapper'
import { registerAllModules } from 'handsontable/registry'
import 'handsontable/styles/handsontable.css'
import 'handsontable/styles/ht-theme-horizon.css'
import { cable } from '../../cable.js'
import { HOT_LICENSE_KEY } from '../utils/handsontableLicense.js'
import { sanitizeHandsontableHtml } from '../utils/handsontableSanitizer.js'

// Read-only Capex Growth tab: each hard-input, non-scheduler Capex line grown
// annually by its growth profile (Y1 ungrown). Mirrors OpexGrowth. Scheduler and
// percentage rows are excluded server-side (capex-developer-note §10).
registerAllModules()

const LEAD_COLUMNS = 5 // Project Name | Input Method | Amount Basis | Base Amount / Rate | Growth Rate
const HEADER_ROW_COUNT = 3
const REALTIME_DEBOUNCE = 1500

function formatAmount(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function yearEndLabel(valuationDate, n) {
  if (!valuationDate) return ''
  const start = new Date(`${valuationDate}T00:00:00Z`)
  if (Number.isNaN(start.getTime())) return ''
  const end = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 12 * n, 1))
  return end.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
}

function buildHeaderRows(valuationDate, years) {
  if (years === 0) return []
  const emptyPeriods = Array.from({ length: years }, () => '')
  const yearRow = ['Analysis Year', '', '', '', '', ...Array.from({ length: years }, (_, i) => `Y${i + 1}`)]
  const periodEndRow = ['Period End', '', '', '', '', ...Array.from({ length: years }, (_, i) => yearEndLabel(valuationDate, i + 1))]
  const colHeaderRow = ['Project Name', 'Input Method', 'Amount Basis', 'Base Amount / Rate', 'Growth Rate', ...emptyPeriods]
  return [yearRow, periodEndRow, colHeaderRow]
}

export default function CapexGrowth({ valuationId, dataUrl }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [years, setYears] = useState(0)
  const [valuationDate, setValuationDate] = useState(null)
  const [rows, setRows] = useState([])
  const fetchRef = useRef(null)

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    setError(null)
    try {
      const res = await fetch(dataUrl, { headers: { Accept: 'application/json' } })
      const json = await res.json()
      if (!res.ok || !json.success) throw new Error(json.error || `Request failed (${res.status})`)
      const data = json.data || {}
      setYears(data.years || 0)
      setValuationDate(data.valuation_date || null)
      setRows(data.rows || [])
    } catch (err) {
      setError(err.message || 'Failed to load')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [dataUrl])

  useEffect(() => { fetchRef.current = fetchData }, [fetchData])
  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    if (!valuationId) return undefined
    let debounceTimer = null
    const subscription = cable.subscriptions.create(
      { channel: 'ValuationChannel', valuation_id: valuationId },
      {
        received() {
          if (debounceTimer) clearTimeout(debounceTimer)
          debounceTimer = setTimeout(() => { fetchRef.current?.(true) }, REALTIME_DEBOUNCE)
        }
      }
    )
    return () => {
      subscription.unsubscribe()
      if (debounceTimer) clearTimeout(debounceTimer)
    }
  }, [valuationId])

  const headerRows = useMemo(() => buildHeaderRows(valuationDate, years), [valuationDate, years])

  const tableData = useMemo(() => {
    const data = rows.map(row => [
      row.name,
      row.input_method,
      row.amount_basis,
      formatAmount(row.base),
      row.growth_rate,
      ...(row.grown || []).map(formatAmount)
    ])
    return [...headerRows, ...data]
  }, [headerRows, rows])

  const mergeCells = useMemo(() => (
    [0, 1].map(r => ({ row: r, col: 0, rowspan: 1, colspan: LEAD_COLUMNS }))
  ), [])

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    )
  }

  if (error) {
    return <div className="alert alert-danger m-3">{error}</div>
  }

  if (rows.length === 0) {
    return (
      <div className="text-muted text-center py-5">
        No hard-input capital expenditure lines to grow. Scheduler and percentage-based lines are excluded.
      </div>
    )
  }

  return (
    <div className="cash-flow-table-container">
      <div className="cash-flow-table-viewport">
        <HotTable
          data={tableData}
          rowHeaders={false}
          colHeaders={false}
          fixedColumnsStart={LEAD_COLUMNS}
          themeName="ht-theme-horizon"
          licenseKey={HOT_LICENSE_KEY}
          sanitizer={sanitizeHandsontableHtml}
          stretchH="none"
          width="100%"
          height="auto"
          readOnly={true}
          manualColumnResize={true}
          manualRowResize={false}
          mergeCells={mergeCells}
          cells={(row, col) => {
            if (row < HEADER_ROW_COUNT - 1) {
              return { readOnly: true, className: 'htRight htMiddle' }
            }
            if (row < HEADER_ROW_COUNT) {
              if (col < LEAD_COLUMNS) return { readOnly: true, className: 'htLeft htMiddle' }
              return { readOnly: true, className: 'htRight htMiddle' }
            }
            if (col < LEAD_COLUMNS) {
              return { readOnly: true, className: col === 3 ? 'htRight' : 'htLeft' }
            }
            return { readOnly: true, className: 'htRight' }
          }}
        />
      </div>
    </div>
  )
}
